/**
 * Legacy Fallback Mappings
 *
 * Maps hidden (legacy) product attributes and hidden module attributes
 * to their corresponding visible module attributes. Used to display
 * legacy values as fallbacks when the real module attribute has no value.
 *
 * Only affects rendering — no database writes happen here.
 */

// ---------------------------------------------------------------------------
// Value-mapping helpers
// ---------------------------------------------------------------------------

const mapAntennaValue = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim().toLowerCase();
  if (v === 'v1.0' || v === '1.0') return 'Passive';
  if (v === 'v1.1' || v === '1.1') return 'Active';
  return raw; // pass-through if unrecognised
};

const mapHwRevisionOudieN = (raw) => {
  if (!raw) return null;
  const num = parseFloat(raw);
  if (!isNaN(num) && num >= 1.41) {
    return 'V1.41 (resistant to high temperature)';
  }
  return 'Old (non resistant to high temperature)';
};

// ---------------------------------------------------------------------------
// Static mapping configuration
// ---------------------------------------------------------------------------

/**
 * Each entry in PRODUCT_ATTR_TO_MODULE maps a hidden product-attribute name
 * to a target visible module + attribute, optionally with a value transform.
 *
 * Shape: { legacyAttrName: { moduleName, attributeName, mapValue? } }
 *
 * moduleName  – the visible module's catalog name
 * attributeName – the target module-attribute name
 * mapValue    – optional (value) => mappedValue function
 */
const OUDIE_PRODUCT_ATTR_TO_MODULE = {
  'Flarm ID': {
    moduleName: 'Oudie N - Fanet module',
    attributeName: 'Flarm ID',
  },
  'Fanet module type': {
    moduleName: 'Oudie N - Fanet module',
    attributeName: 'Oudie N - Fanet module version',
  },
  'Antenna type Oudie N': {
    moduleName: 'Oudie N - GPS antena',
    attributeName: 'Oudie N - GPS antenna version',
    mapValue: mapAntennaValue,
  },
  'Oudie housing type': {
    moduleName: 'Oudie N - Housing',
    attributeName: 'Oudie N - Housing version',
  },
  'HW revision Oudie N': {
    moduleName: 'Oudie N - Security mechanism',
    attributeName: 'Security PCBA version',
    mapValue: mapHwRevisionOudieN,
  },
};

const OMNI_PRODUCT_ATTR_TO_MODULE = {
  'Flarm ID': {
    moduleName: 'Omni - Fanet module',
    attributeName: 'Flarm ID',
  },
  'HW revision OMNI': {
    moduleName: 'Omni - Housing',
    attributeName: 'Omni - Housing version',
  },
  'Omni Battery Series': {
    moduleName: 'Omni - Battery',
    attributeName: 'Omni - Battery version',
  },
};

/**
 * MODULE_ATTR_TO_MODULE maps hidden-module attributes to visible-module
 * attributes.
 *
 * Shape: { hiddenModuleName: { legacyAttrName: { moduleName, attributeName } } }
 */
const OUDIE_MODULE_ATTR_TO_MODULE = {
  'Legacy Oudie - LCD': {
    'LCD Series': { moduleName: 'Oudie N - LCD', attributeName: 'Oudie N - LCD version' },
  },
  'LCD': {
    'LCD Series': { moduleName: 'Oudie N - LCD', attributeName: 'Oudie N - LCD version' },
  },
  'Legacy Oudie - Battery': {
    'Battery Series': { moduleName: 'Oudie N - Battery', attributeName: 'Oudie N - Battery Series' },
  },
  'Battery': {
    'Battery Series': { moduleName: 'Oudie N - Battery', attributeName: 'Oudie N - Battery Series' },
  },
};

// Omni has no hidden-module-to-visible-module mappings
const OMNI_MODULE_ATTR_TO_MODULE = {};

// ---------------------------------------------------------------------------
// Product-family detection
// ---------------------------------------------------------------------------

const OUDIE_PRODUCT_NAMES = [
  'Oudie N IGC',
  'Oudie N (F)',
  'Oudie N (F+)',
  'Oudie N (G)',
];

const OMNI_PRODUCT_NAMES = ['Omni'];

const isOudie = (productName) =>
  OUDIE_PRODUCT_NAMES.some((n) => productName === n);

const isOmni = (productName) =>
  OMNI_PRODUCT_NAMES.some((n) => productName === n);

// ---------------------------------------------------------------------------
// Helper: build a name->id map for visible modules from productConfig
// ---------------------------------------------------------------------------

const buildModuleNameToIdMap = (assignedModules) => {
  const map = {};
  (assignedModules || []).forEach((m) => {
    map[m.name] = String(m.id);
  });
  return map;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compute fallback values for visible module attributes that currently have
 * no real value stored, but DO have a matching legacy attribute value.
 *
 * @param {Object} productConfig
 *   { product, productAttributes, assignedModules, moduleAttributesMap }
 * @param {Object} deviceAttributeValues
 *   Map of product-attribute name -> value (from device.attributes)
 * @param {Object} allModuleAttributeValues
 *   Map of `${moduleId}_${attrName}` -> value (ALL module attrs, including hidden)
 * @returns {Object} Map of `${visibleModuleId}_${attrName}` -> fallback value
 */
export function computeFallbackValues(
  productConfig,
  deviceAttributeValues = {},
  allModuleAttributeValues = {}
) {
  if (!productConfig || !productConfig.product) return {};

  const productName = productConfig.product.name;
  let productAttrMap = null;
  let moduleAttrMap = null;

  if (isOudie(productName)) {
    productAttrMap = OUDIE_PRODUCT_ATTR_TO_MODULE;
    moduleAttrMap = OUDIE_MODULE_ATTR_TO_MODULE;
  } else if (isOmni(productName)) {
    productAttrMap = OMNI_PRODUCT_ATTR_TO_MODULE;
    moduleAttrMap = OMNI_MODULE_ATTR_TO_MODULE;
  } else {
    return {};
  }

  const nameToId = buildModuleNameToIdMap(productConfig.assignedModules);
  const fallbacks = {};

  // 1. Product attribute -> visible module attribute
  if (productAttrMap) {
    Object.entries(productAttrMap).forEach(([legacyName, target]) => {
      const legacyValue = deviceAttributeValues[legacyName];
      if (!legacyValue || (typeof legacyValue === 'string' && !legacyValue.trim())) {
        return;
      }

      const targetModuleId = nameToId[target.moduleName];
      if (!targetModuleId) return; // product doesn't have this visible module

      const targetKey = `${targetModuleId}_${target.attributeName}`;

      // Only fallback if the real module attribute is empty
      const realValue = allModuleAttributeValues[targetKey];
      if (realValue && (typeof realValue !== 'string' || realValue.trim())) {
        return;
      }

      const mapped = target.mapValue ? target.mapValue(legacyValue) : legacyValue;
      if (mapped != null) {
        fallbacks[targetKey] = mapped;
      }
    });
  }

  // 2. Hidden module attribute -> visible module attribute
  if (moduleAttrMap) {
    // Build a reverse map: hiddenModuleName -> hiddenModuleId
    const hiddenModuleNameToId = {};
    (productConfig.assignedModules || []).forEach((m) => {
      if (m.hidden) {
        hiddenModuleNameToId[m.name] = String(m.id);
      }
    });

    Object.entries(moduleAttrMap).forEach(([hiddenModuleName, attrMappings]) => {
      const hiddenModuleId = hiddenModuleNameToId[hiddenModuleName];
      if (!hiddenModuleId) return;

      Object.entries(attrMappings).forEach(([legacyAttrName, target]) => {
        const legacyKey = `${hiddenModuleId}_${legacyAttrName}`;
        const legacyValue = allModuleAttributeValues[legacyKey];
        if (!legacyValue || (typeof legacyValue === 'string' && !legacyValue.trim())) {
          return;
        }

        const targetModuleId = nameToId[target.moduleName];
        if (!targetModuleId) return;

        const targetKey = `${targetModuleId}_${target.attributeName}`;
        const realValue = allModuleAttributeValues[targetKey];
        if (realValue && (typeof realValue !== 'string' || realValue.trim())) {
          return;
        }

        const mapped = target.mapValue ? target.mapValue(legacyValue) : legacyValue;
        if (mapped != null) {
          fallbacks[targetKey] = mapped;
        }
      });
    });
  }

  return fallbacks;
}
